import { selectAuth } from '@brands/store/selectors/auth';
import { AuthState } from '@brands/store/slices/authSlice';
import { Client } from '@twilio/conversations';
import React, { createContext, FC, ReactNode, useCallback, useEffect, useState } from 'react';

import { getConversationToken } from '../../services/communication/getConversationToken';
import { useJwtExpired } from '../useJwtExpired';
import { useAppSelector } from '../useReduxHook';

/**
 * Context holding the app-wide Twilio Conversations Client singleton.
 * Consumed via `useTwilioConversationsClient`.
 *
 * Prior to DO-3975, `useTwilioConversationsClient` held the `Client`
 * in component-local `useState`, which meant every consumer site
 * instantiated its own `Client` — and therefore opened its own
 * WebSocket — for the same logged-in user. Hoisting the state into
 * a single Provider at the App root collapses every consumer down
 * to one shared Client. New consumers (e.g. the Sidenav's realtime
 * unread-badge listener added in this PR) no longer pile on extra
 * connections.
 *
 * The Provider owns the token-refresh + client-renewal lifecycle
 * (previously inside the hook). Behavior is preserved:
 *   - Mounts above `<Routing />` so it's alive for both logged-out
 *     and logged-in flows; emits `null` while the user is logged out
 *     or the initial token is being minted.
 *   - Watches `userData` to (re)mint the token on login.
 *   - Watches `token` to (re)create or update the `Client`.
 *   - Wires `useJwtExpired` so the token auto-renews before it dies.
 */
export const TwilioConversationsClientContext = createContext<Client | null>(null);

interface TwilioConversationsClientProviderProps {
  children: ReactNode;
}

export const TwilioConversationsClientProvider: FC<TwilioConversationsClientProviderProps> = ({ children }) => {
  const userData: AuthState = useAppSelector(selectAuth);
  const [token, setToken] = useState<string | null>(null);
  const [client, setClient] = useState<Client | null>(null);

  const renewToken = useCallback(async (): Promise<void> => {
    const newToken = await getConversationToken(userData.userInfo?.role?.name);
    setToken(newToken);
  }, [userData.userInfo?.role?.name]);

  async function renewClient(): Promise<void> {
    if (client) {
      if (token) {
        await client.updateToken(token);
      } else {
        await client.shutdown();
      }
    } else if (token) {
      setClient(new Client(token));
    } else {
      setClient(null);
    }
  }

  useEffect(() => {
    if (userData.userInfo) {
      renewToken();
    } else {
      setToken(null);
    }
  }, [userData]);

  useEffect(() => {
    renewClient();
  }, [token]);

  useJwtExpired(renewToken, token);

  return (
    <TwilioConversationsClientContext.Provider value={client}>{children}</TwilioConversationsClientContext.Provider>
  );
};

export default TwilioConversationsClientProvider;
